import React, { useCallback, useEffect, useRef, useState } from "react";
import "./Testimonials.css";
import Container from "../Container";

const TESTIMONIALS = [
  { id: "testimony1", src: "/assets/testimony1.mp4", poster: "/assets/testimony1-poster.webp" },
  { id: "testimony2", src: "/assets/testimony2.mp4", poster: "/assets/testimony2-poster.webp" },
  { id: "testimony3", src: "/assets/testimony3.mp4", poster: "/assets/testimony3-poster.webp" },
  { id: "testimony4", src: "/assets/testimony4.mp4", poster: "/assets/testimony4-poster.webp" },
];

const LABEL_DELAY = 3000;
const PREVIEW_DURATION = 6000;

const Testimonials = () => {
  const [previewId, setPreviewId] = useState(null);
  const [readyToWatchId, setReadyToWatchId] = useState(null);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const videoRefs = useRef({});
  const trackRef = useRef(null);
  const soundEnabledRef = useRef(false);

  const startPreview = useCallback((id) => {
    setPreviewId(id);
  }, []);

  const stopPreview = useCallback((id) => {
    setPreviewId((currentId) => (currentId === id ? null : currentId));
    setReadyToWatchId((currentId) => (currentId === id ? null : currentId));
  }, []);

  useEffect(() => {
    if (!previewId) return undefined;

    const video = videoRefs.current[previewId];
    if (!video) return undefined;

    setReadyToWatchId(null);
    Object.entries(videoRefs.current).forEach(([id, item]) => {
      if (id !== previewId) {
        item.pause();
        item.currentTime = 0;
      }
    });

    const playPreview = () => {
      video.currentTime = 0;
      video.muted = !soundEnabledRef.current;
      video.play().catch(() => {
        video.muted = true;
        video.play().catch(() => {});
      });
    };

    if (video.readyState >= 1) {
      playPreview();
    } else {
      video.addEventListener("loadedmetadata", playPreview, { once: true });
    }

    const labelTimer = window.setTimeout(() => {
      setReadyToWatchId(previewId);
    }, LABEL_DELAY);
    const stopTimer = window.setTimeout(() => {
      video.pause();
    }, PREVIEW_DURATION);

    return () => {
      window.clearTimeout(labelTimer);
      window.clearTimeout(stopTimer);
      video.pause();
      video.removeEventListener("loadedmetadata", playPreview);
    };
  }, [previewId]);

  useEffect(() => {
    if (!selectedTestimonial) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") setSelectedTestimonial(null);
    };
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [selectedTestimonial]);

  const previewCenteredVideo = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    const trackCenter = track.getBoundingClientRect().left + track.clientWidth / 2;
    const centeredCard = TESTIMONIALS.reduce((closest, testimonial) => {
      const card = document.getElementById(`testimonial-${testimonial.id}`);
      if (!card) return closest;

      const cardBounds = card.getBoundingClientRect();
      const distance = Math.abs(cardBounds.left + cardBounds.width / 2 - trackCenter);
      return !closest || distance < closest.distance ? { id: testimonial.id, distance } : closest;
    }, null);

    if (centeredCard) startPreview(centeredCard.id);
  }, [startPreview]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    let animationFrame;
    const handleScroll = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(previewCenteredVideo);
    };

    previewCenteredVideo();
    track.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", previewCenteredVideo);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      track.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", previewCenteredVideo);
    };
  }, [previewCenteredVideo]);

  const openTestimonial = (testimonial) => {
    setPreviewId(null);
    setReadyToWatchId(null);
    setSelectedTestimonial(testimonial);
  };

  const moveToTestimonial = (direction) => {
    const track = trackRef.current;
    if (!track) return;

    const cards = Array.from(track.querySelectorAll(".testimonial-card"));
    const trackCenter = track.scrollLeft + track.clientWidth / 2;
    const currentIndex = cards.reduce((closestIndex, card, index) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const closestCenter = cards[closestIndex].offsetLeft + cards[closestIndex].offsetWidth / 2;
      return Math.abs(cardCenter - trackCenter) < Math.abs(closestCenter - trackCenter)
        ? index
        : closestIndex;
    }, 0);
    const nextCard = cards[Math.max(0, Math.min(cards.length - 1, currentIndex + direction))];

    track.scrollTo({
      behavior: "smooth",
      left: nextCard.offsetLeft - (track.clientWidth - nextCard.offsetWidth) / 2,
    });
  };

  return (
    <Container>
      <section
        className="testimonials section-spacing"
        aria-labelledby="testimonials-title"
        onKeyDown={() => {
          soundEnabledRef.current = true;
        }}
        onPointerDown={() => {
          soundEnabledRef.current = true;
        }}
      >
        <div className="testimonials-heading">
          <h2 id="testimonials-title">Testimonios</h2>
          <p>Conozca las experiencias de nuestros pacientes.</p>
        </div>

        <div className="testimonials-carousel">
          <button
            aria-label="Testimonio anterior"
            className="testimonial-navigation testimonial-navigation-previous"
            onClick={() => moveToTestimonial(-1)}
            type="button"
          >
            ←
          </button>
          <div className="testimonials-track" ref={trackRef}>
            {TESTIMONIALS.map((testimonial) => (
              <button
                className="testimonial-card"
                id={`testimonial-${testimonial.id}`}
                key={testimonial.id}
                onBlur={() => stopPreview(testimonial.id)}
                onClick={() => openTestimonial(testimonial)}
                onFocus={() => startPreview(testimonial.id)}
                onMouseEnter={() => startPreview(testimonial.id)}
                onMouseLeave={() => stopPreview(testimonial.id)}
                type="button"
              >
                <video
                  aria-hidden="true"
                  playsInline
                  poster={testimonial.poster}
                  preload="metadata"
                  ref={(element) => {
                    videoRefs.current[testimonial.id] = element;
                  }}
                  src={testimonial.src}
                />
                <span className="testimonial-play" aria-hidden="true">▶</span>
                {readyToWatchId === testimonial.id && (
                  <span className="testimonial-cta">Ver testimonio completo</span>
                )}
                <span className="sr-only">Ver testimonio completo</span>
              </button>
            ))}
          </div>
          <button
            aria-label="Siguiente testimonio"
            className="testimonial-navigation testimonial-navigation-next"
            onClick={() => moveToTestimonial(1)}
            type="button"
          >
            →
          </button>
        </div>
      </section>

      {selectedTestimonial && (
        <div className="testimonial-modal" onClick={() => setSelectedTestimonial(null)} role="presentation">
          <div
            aria-label="Testimonio en video"
            aria-modal="true"
            className="testimonial-modal-content"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
          >
            <button
              aria-label="Cerrar video"
              className="testimonial-modal-close"
              onClick={() => setSelectedTestimonial(null)}
              type="button"
            >
              ×
            </button>
            <video autoPlay controls playsInline src={selectedTestimonial.src}>
              Su navegador no admite la reproducción de video.
            </video>
          </div>
        </div>
      )}
    </Container>
  );
};

export default Testimonials;
